import type { FC, ReactNode, PointerEvent as ReactPointerEvent, KeyboardEvent as ReactKeyboardEvent } from 'react'
import type { DockAppId } from '@/constants/atelier-data'
import type { OpenWindow, WindowCtx, WindowKind } from '@/components/landing/atelier/windows'

import { Fragment, useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'

import { cn } from '@/lib/utils'

import {
    AppGlyph,
    BellSlashGlyph,
    IOSBatteryGlyph,
    MacBatteryGlyph,
    SignalBarsGlyph,
    WifiGlyph
} from '@/components/landing/atelier/icons'

import {
    DEFAULT_WINDOW_SIZES,
    PhotoLightbox,
    WindowView
} from '@/components/landing/atelier/windows'

import {
    DOCK_APPS,
    PROJECTS,
    WALLPAPER_DARK,
    WALLPAPER_LIGHT
} from '@/constants/atelier-data'

interface TileItem {
    id: string
    title: string
    isFolder: boolean
    thumbnailSrc?: string
    thumbnailIsIcon?: boolean
    pos: { xPct: number; yPct: number }
}

const TILES: TileItem[] = [
    ...PROJECTS.map((p) => ({
        id: p.id,
        title: p.title,
        isFolder: false,
        thumbnailSrc: p.thumbnailSrc,
        thumbnailIsIcon: p.thumbnailIsIcon,
        pos: p.pos
    })),
    { id: 'dont-open', title: "Don't Open", isFolder: true, pos: { xPct: 6, yPct: 60 } }
]

const HERO_ID = PROJECTS[0].id
const MOBILE_PRIMARY: DockAppId[] = ['youtube', 'notes', 'mail']
const GROUP_APPS: DockAppId[] = ['instagram', 'photos', 'x', 'trash']

const DOCK_ICON_BASE = 56
const DOCK_ICON_MAX = 96
const DOCK_HOVER_RANGE = 140
const DOCK_SIZE_LERP = 0.28
const MOBILE_ICON_BASE = 72

const VEIL_GRADIENT =
    'radial-gradient(120% 90% at 50% 30%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.18) 70%, rgba(0,0,0,0.35) 100%)'

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

interface OpenSpec {
    kind: WindowKind
    projectId?: string
    folderName?: string
    title?: string
}

export const Atelier: FC = (): ReactNode => {
    const [mounted, setMounted] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [isDark, setIsDark] = useState(false)
    const [tilePositions, setTilePositions] = useState<Record<string, { x: number; y: number; z: number }>>({})
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [entranceDone, setEntranceDone] = useState(false)
    const [windows, setWindows] = useState<OpenWindow[]>([])
    const [lightbox, setLightbox] = useState<string | null>(null)
    const [groupPanelOpen, setGroupPanelOpen] = useState(false)

    const sectionRef = useRef<HTMLElement>(null)
    const wallpaperWrapRef = useRef<HTMLDivElement>(null)
    const menuBarRef = useRef<HTMLDivElement>(null)
    const dockRef = useRef<HTMLDivElement>(null)
    const featuredThumbRef = useRef<HTMLDivElement>(null)
    const featuredImgRef = useRef<HTMLImageElement>(null)
    const groupBackdropRef = useRef<HTMLDivElement>(null)
    const groupPanelRef = useRef<HTMLDivElement>(null)

    const tileRefs = useRef<Map<string, HTMLDivElement>>(new Map())
    const isMobileRef = useRef(false)
    const winCounter = useRef(0)
    const entranceStartedRef = useRef(false)

    const dockIconEls = useRef<(HTMLDivElement | null)[]>([])
    const dockCursor = useRef({ x: -9999, active: false, smooth: -9999 })
    const iconSizes = useRef<number[]>([])
    const rafId = useRef<number | null>(null)

    const cycleTimeout = useRef<number | null>(null)
    const cycleInterval = useRef<number | null>(null)
    const timelineRef = useRef<ReturnType<typeof gsap.timeline> | null>(null)

    const dragRef = useRef({
        id: null as string | null,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
        moved: false,
        pointerId: -1,
        el: null as HTMLDivElement | null,
        z: 0,
        lastX: 0,
        lastY: 0
    })

    /* -------------------------------- window manager -------------------------------- */

    const openWindow = (spec: OpenSpec): void => {
        const sec = sectionRef.current
        const sw = sec?.clientWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1280)
        const sh = sec?.clientHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 800)
        const mobile = isMobileRef.current
        const base = DEFAULT_WINDOW_SIZES[spec.kind]

        let w: number
        let h: number
        let x: number
        let y: number

        if (mobile) {
            const menuBar = 48
            const topGap = 12
            const dockBand = 128
            const fullH = Math.max(220, sh - menuBar - topGap - dockBand)
            w = sw - 32
            x = 16
            y = 60
            h = fullH

            if (spec.kind === 'video') {
                const aspectH = Math.round((w * 9) / 16) + 36
                h = Math.min(aspectH, fullH)
                y = 60 + Math.max(0, (fullH - h) / 2)
            }
        } else {
            w = Math.min(base.w, sw - 16)
            h = Math.min(base.h, sh - 100)
            const offset = Math.min(windows.length * 28, 140)
            x = clamp((sw - w) / 2 + offset, 8, Math.max(8, sw - w - 8))
            y = clamp((sh - h) / 2 + offset, 28, Math.max(28, sh - h - 100))
        }

        const uid = ++winCounter.current

        setWindows((prev) => {
            const z = prev.reduce((m, win) => Math.max(m, win.z), 100) + 1
            const title = spec.title ?? spec.folderName ?? ''

            return [
                ...prev,
                { uid, kind: spec.kind, projectId: spec.projectId, folderName: spec.folderName, title, x, y, w, h, z }
            ]
        })
    }

    const closeWindow = (uid: number): void => {
        setWindows((prev) => prev.filter((w) => w.uid !== uid))
    }

    const focusWindow = (uid: number): void => {
        setWindows((prev) => {
            const max = prev.reduce((m, w) => Math.max(m, w.z), 100)

            return prev.map((w) => (w.uid === uid ? { ...w, z: max + 1 } : w))
        })
    }

    const moveWindow = (uid: number, x: number, y: number): void => {
        setWindows((prev) => prev.map((w) => (w.uid === uid ? { ...w, x, y } : w)))
    }

    const ctx: WindowCtx = {
        isMobile,
        openProject: (projectId) => openWindow({ kind: 'project', projectId }),
        openVideo: (title) => openWindow({ kind: 'video', title }),
        openLightbox: (src) => setLightbox(src),
        closeWindow
    }

    const openTile = (item: TileItem): void => {
        if (item.isFolder) {
            openWindow({ kind: 'finder', folderName: "Don't Open" })
        } else {
            openWindow({ kind: 'project', projectId: item.id })
        }
    }

    // Dock apps with an `href` (YouTube, Instagram) render as external links in
    // the docks; the mobile "More" panel falls back to opening them here.
    const handleDockApp = (id: DockAppId): void => {
        const link = DOCK_APPS.find((a) => a.id === id)?.href
        if (link) {
            window.open(link, '_blank', 'noopener,noreferrer')
            return
        }

        switch (id) {
            case 'notes':
                openWindow({ kind: 'notes' })
                break
            case 'photos':
                openWindow({ kind: 'photos' })
                break
            case 'x':
                openWindow({ kind: 'tweet' })
                break
            case 'mail':
                openWindow({ kind: 'compose' })
                break
            case 'trash':
                openWindow({ kind: 'finder', folderName: 'Trash' })
                break
        }
    }

    /* -------------------------------- tile positions -------------------------------- */

    const seedPositions = (): void => {
        const sec = sectionRef.current
        const vw = sec?.clientWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1280)
        const vh = sec?.clientHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 800)

        setTilePositions((prev) => {
            const next: Record<string, { x: number; y: number; z: number }> = {}

            TILES.forEach((tile, i) => {
                const existing = prev[tile.id]
                const baseX = existing ? existing.x : (tile.pos.xPct / 100) * vw
                const baseY = existing ? existing.y : (tile.pos.yPct / 100) * vh
                next[tile.id] = {
                    x: clamp(baseX, 8, Math.max(8, vw - 88)),
                    y: clamp(baseY, 8, Math.max(8, vh - 236)),
                    z: existing ? existing.z : 10 + i
                }
            })

            return next
        })
    }

    /* -------------------------------- mount + responsive -------------------------------- */

    useEffect(() => {
        isMobileRef.current = window.matchMedia('(max-width: 767px)').matches
        setIsMobile(isMobileRef.current)
        setIsDark(document.documentElement.getAttribute('data-theme') === 'dark')

        if (wallpaperWrapRef.current) {
            if (isMobileRef.current) {
                gsap.set(wallpaperWrapRef.current, { opacity: 0 })
            } else {
                gsap.set(wallpaperWrapRef.current, { filter: 'blur(24px)', scale: 1.04 })
            }
        }

        setMounted(true)
    }, [])

    useEffect(() => {
        if (mounted) seedPositions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted])

    useEffect(() => {
        if (!mounted) return

        const mq = window.matchMedia('(max-width: 767px)')
        const onChange = (): void => {
            isMobileRef.current = mq.matches
            setIsMobile(mq.matches)
            seedPositions()
        }
        const onResize = (): void => seedPositions()

        mq.addEventListener('change', onChange)
        window.addEventListener('resize', onResize)

        return () => {
            mq.removeEventListener('change', onChange)
            window.removeEventListener('resize', onResize)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted])

    /* -------------------------------- theme observer -------------------------------- */

    useEffect(() => {
        const el = document.documentElement
        const obs = new MutationObserver(() => {
            setIsDark(el.getAttribute('data-theme') === 'dark')
        })
        obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] })

        return () => obs.disconnect()
    }, [])

    /* -------------------------------- cinematic intro -------------------------------- */

    useEffect(() => {
        if (!mounted) return
        if (entranceStartedRef.current) return
        if (Object.keys(tilePositions).length === 0) return

        const sec = sectionRef.current
        if (!sec) return

        entranceStartedRef.current = true

        const mobile = isMobileRef.current
        const wrap = wallpaperWrapRef.current
        const menu = menuBarRef.current
        const dock = dockRef.current
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
        timelineRef.current = tl

        if (mobile) {
            if (wrap) tl.fromTo(wrap, { opacity: 0, scale: 1.04 }, { opacity: 1, scale: 1, duration: 0.45 }, 0)
            if (menu) tl.fromTo(menu, { y: -28, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45 }, 0.05)

            TILES.forEach((tile, i) => {
                const el = tileRefs.current.get(tile.id)
                if (!el) return
                tl.fromTo(
                    el,
                    { opacity: 0, y: 10, scale: 0.94 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.5 },
                    0.2 + 0.04 * (i % 10)
                )
            })

            if (dock) tl.fromTo(dock, { y: 60, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 }, 0.35)
            tl.call(() => setEntranceDone(true), undefined, 1.0)
            return
        }

        const sw = sec.clientWidth
        const sh = sec.clientHeight
        const centreX = sw / 2 - 48
        const centreY = sh / 2 - 58
        const thumb = featuredThumbRef.current
        const heroPos = tilePositions[HERO_ID]
        const heroEl = tileRefs.current.get(HERO_ID)

        if (wrap) {
            tl.set(wrap, { filter: 'blur(24px)', scale: 1.04 }, 0)
            tl.to(wrap, { filter: 'blur(0px)', scale: 1, duration: 0.4 }, 1.5)
        }

        if (thumb) {
            gsap.set(thumb, { xPercent: -50, yPercent: -50 })
            tl.fromTo(
                thumb,
                { y: 320, opacity: 0, scale: 0.85, filter: 'blur(8px)' },
                { y: 0, opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.55 },
                0
            )
        }

        TILES.forEach((tile, i) => {
            if (tile.id === HERO_ID) return
            const el = tileRefs.current.get(tile.id)
            const pos = tilePositions[tile.id]
            if (!el || !pos) return
            tl.fromTo(
                el,
                { opacity: 0, x: centreX, y: centreY, scale: 0.35, filter: 'blur(8px)' },
                { opacity: 1, x: pos.x, y: pos.y, scale: 1, filter: 'blur(0px)', duration: 0.6 },
                1.7 + 0.02 * (i % 10)
            )
        })

        if (thumb && heroPos) {
            tl.to(
                thumb,
                {
                    x: heroPos.x + 48 - sw / 2,
                    y: heroPos.y + 39 - sh / 2,
                    scale: 78 / 180,
                    duration: 0.4,
                    ease: 'power3.inOut'
                },
                2.0
            )
        }

        if (thumb) tl.to(thumb, { opacity: 0, duration: 0.15 }, 2.4)
        if (heroEl) tl.to(heroEl, { opacity: 1, duration: 0.15 }, 2.4)
        if (menu) tl.fromTo(menu, { y: -32, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 }, 2.5)
        if (dock) tl.fromTo(dock, { y: 80, opacity: 0 }, { y: 0, opacity: 1, duration: 0.55 }, 2.7)
        tl.call(() => setEntranceDone(true), undefined, 3.25)

        const thumbs = PROJECTS.map((p) => p.thumbnailSrc)
        thumbs.forEach((src) => {
            const img = new window.Image()
            img.src = src
        })

        const showFeatured = (index: number): void => {
            const img = featuredImgRef.current
            const project = PROJECTS[index]
            if (!img || !project) return
            img.src = project.thumbnailSrc
            img.style.objectFit = project.thumbnailIsIcon ? 'contain' : 'cover'
            img.style.padding = project.thumbnailIsIcon ? '16%' : '0'
        }

        let frame = 0
        cycleTimeout.current = window.setTimeout(() => {
            cycleInterval.current = window.setInterval(() => {
                frame += 1
                if (frame >= thumbs.length) {
                    if (cycleInterval.current) window.clearInterval(cycleInterval.current)
                    showFeatured(0)
                    return
                }
                showFeatured(frame)
            }, 130)
        }, 400)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted, tilePositions])

    useEffect(() => {
        return () => {
            timelineRef.current?.kill()
            if (cycleTimeout.current) window.clearTimeout(cycleTimeout.current)
            if (cycleInterval.current) window.clearInterval(cycleInterval.current)
        }
    }, [])

    /* -------------------------------- dock magnification -------------------------------- */

    useEffect(() => {
        const count = DOCK_APPS.length
        iconSizes.current = new Array(count).fill(DOCK_ICON_BASE)

        const tick = (): void => {
            if (!isMobileRef.current) {
                const cursor = dockCursor.current
                cursor.smooth += (cursor.x - cursor.smooth) * 0.22

                for (let i = 0; i < count; i += 1) {
                    const el = dockIconEls.current[i]
                    if (!el) continue
                    const rect = el.getBoundingClientRect()
                    const cx = rect.left + rect.width / 2
                    const dist = Math.abs(cursor.smooth - cx)
                    const t = Math.max(0, 1 - dist / DOCK_HOVER_RANGE)
                    const ease = t * t * (3 - 2 * t)
                    const target = cursor.active ? DOCK_ICON_BASE + (DOCK_ICON_MAX - DOCK_ICON_BASE) * ease : DOCK_ICON_BASE
                    iconSizes.current[i] += (target - iconSizes.current[i]) * DOCK_SIZE_LERP
                    el.style.width = `${iconSizes.current[i].toFixed(2)}px`
                    el.style.height = `${iconSizes.current[i].toFixed(2)}px`
                }
            }

            rafId.current = requestAnimationFrame(tick)
        }

        rafId.current = requestAnimationFrame(tick)

        return () => {
            if (rafId.current) cancelAnimationFrame(rafId.current)
        }
    }, [])

    /* -------------------------------- group panel entrance -------------------------------- */

    useEffect(() => {
        if (!groupPanelOpen) return
        if (groupBackdropRef.current) {
            gsap.fromTo(groupBackdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.22 })
        }
        if (groupPanelRef.current) {
            gsap.fromTo(
                groupPanelRef.current,
                { opacity: 0, scale: 0.86, filter: 'blur(8px)' },
                { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.34, ease: 'power3.out' }
            )
        }
    }, [groupPanelOpen])

    /* -------------------------------- global ESC -------------------------------- */

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return

            if (lightbox) {
                setLightbox(null)
                return
            }
            if (groupPanelOpen) {
                setGroupPanelOpen(false)
                return
            }
            if (windows.length > 0) {
                const top = windows.reduce((a, b) => (b.z > a.z ? b : a))
                closeWindow(top.uid)
                return
            }
            if (selectedId) setSelectedId(null)
        }

        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [lightbox, groupPanelOpen, windows, selectedId])

    /* -------------------------------- tile drag -------------------------------- */

    const onTilePointerDown = (e: ReactPointerEvent<HTMLDivElement>, item: TileItem): void => {
        if (isMobileRef.current) {
            dragRef.current = {
                ...dragRef.current,
                id: item.id,
                startX: e.clientX,
                startY: e.clientY,
                moved: false,
                el: null,
                pointerId: -1
            }
            return
        }

        if (e.button !== 0) return
        e.preventDefault()
        const el = e.currentTarget
        el.setPointerCapture(e.pointerId)

        const pos = tilePositions[item.id]
        const newZ = Math.max(...Object.values(tilePositions).map((p) => p.z), 10) + 1
        el.style.zIndex = String(newZ)

        dragRef.current = {
            id: item.id,
            startX: e.clientX,
            startY: e.clientY,
            originX: pos?.x ?? 0,
            originY: pos?.y ?? 0,
            moved: false,
            pointerId: e.pointerId,
            el,
            z: newZ,
            lastX: pos?.x ?? 0,
            lastY: pos?.y ?? 0
        }
        setSelectedId(item.id)
    }

    const onTilePointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
        const d = dragRef.current
        if (!d.id) return

        const dx = e.clientX - d.startX
        const dy = e.clientY - d.startY
        if (!d.moved && Math.hypot(dx, dy) > 4) d.moved = true
        if (!d.moved) return
        if (isMobileRef.current || !d.el) return

        const sec = sectionRef.current
        if (!sec) return

        const nx = clamp(d.originX + dx, 4, sec.clientWidth - 100)
        const ny = clamp(d.originY + dy, 4, sec.clientHeight - 226)
        d.lastX = nx
        d.lastY = ny
        d.el.style.transform = `translate3d(${nx}px, ${ny}px, 0)`
    }

    const onTilePointerUp = (e: ReactPointerEvent<HTMLDivElement>, item: TileItem): void => {
        const d = dragRef.current
        const wasMoved = d.moved

        if (!isMobileRef.current && d.el && d.pointerId >= 0 && d.el.hasPointerCapture(d.pointerId)) {
            d.el.releasePointerCapture(d.pointerId)
        }

        if (!wasMoved) {
            dragRef.current.id = null
            openTile(item)
            return
        }

        if (!isMobileRef.current) {
            setTilePositions((prev) => ({ ...prev, [item.id]: { x: d.lastX, y: d.lastY, z: d.z } }))
        }
        dragRef.current.id = null
    }

    const onTileKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>, item: TileItem): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openTile(item)
        }
    }

    const dockPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
        dockCursor.current.x = e.clientX
        if (!dockCursor.current.active) {
            dockCursor.current.smooth = e.clientX
            dockCursor.current.active = true
        }
    }

    const dockPointerLeave = (): void => {
        dockCursor.current.active = false
        dockCursor.current.x = -9999
    }

    /* -------------------------------- tile render -------------------------------- */

    const renderTileInner = (item: TileItem, size: number): ReactNode => {
        const selected = selectedId === item.id

        return (
            <>
                {item.isFolder ? (
                    <div
                        className={cn('relative', selected && 'drop-shadow-[0_0_8px_rgba(255,255,255,0.55)]')}
                        style={{ width: size, height: size }}
                    >
                        <AppGlyph id='folder' />
                    </div>
                ) : (
                    <div
                        className={cn(
                            'relative overflow-hidden rounded-[14px] border transition-[box-shadow,transform] duration-150 ease-out',
                            item.thumbnailIsIcon ? 'bg-white' : 'bg-[rgba(255,255,255,0.04)]',
                            selected
                                ? 'border-white/70 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.6),0_0_0_2px_rgba(255,255,255,0.15)]'
                                : 'border-white/15 shadow-[0_12px_24px_-10px_rgba(0,0,0,0.6)]'
                        )}
                        style={{ width: size, height: size }}
                    >
                        <img
                            src={item.thumbnailSrc}
                            alt={item.title}
                            draggable={false}
                            className={cn('size-full', item.thumbnailIsIcon ? 'object-contain p-[16%]' : 'object-cover')}
                        />
                    </div>
                )}

                <div
                    className={cn(
                        'mt-2 max-w-[110px] truncate rounded-[5px] px-1.5 py-[1px] text-center text-[11px] font-medium leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]',
                        selected ? 'bg-[#3b82f6] text-white' : 'text-white'
                    )}
                >
                    {item.title}
                </div>
            </>
        )
    }

    const renderDesktopTile = (item: TileItem, index: number): ReactNode => {
        const pos = tilePositions[item.id]

        return (
            <div
                key={item.id}
                data-atelier-tile
                data-tile-id={item.id}
                role='button'
                tabIndex={0}
                aria-label={item.title}
                ref={(el) => {
                    if (el) tileRefs.current.set(item.id, el)
                    else tileRefs.current.delete(item.id)
                }}
                onPointerDown={(e) => onTilePointerDown(e, item)}
                onPointerMove={onTilePointerMove}
                onPointerUp={(e) => onTilePointerUp(e, item)}
                onKeyDown={(e) => onTileKeyDown(e, item)}
                className='absolute z-[4] flex cursor-grab select-none flex-col items-center opacity-0 outline-none active:cursor-grabbing'
                style={{
                    width: 96,
                    transform: pos ? `translate3d(${pos.x}px, ${pos.y}px, 0)` : 'translate3d(0px, 0px, 0)',
                    zIndex: pos?.z ?? 10 + index,
                    touchAction: 'none'
                }}
            >
                {renderTileInner(item, 78)}
            </div>
        )
    }

    const renderMobileTile = (item: TileItem): ReactNode => (
        <div
            key={item.id}
            data-atelier-tile
            data-tile-id={item.id}
            role='button'
            tabIndex={0}
            aria-label={item.title}
            ref={(el) => {
                if (el) tileRefs.current.set(item.id, el)
                else tileRefs.current.delete(item.id)
            }}
            onPointerDown={(e) => onTilePointerDown(e, item)}
            onPointerMove={onTilePointerMove}
            onPointerUp={(e) => onTilePointerUp(e, item)}
            onKeyDown={(e) => onTileKeyDown(e, item)}
            className='flex select-none flex-col items-center opacity-0 outline-none'
            style={{ touchAction: 'manipulation' }}
        >
            {renderTileInner(item, 64)}
        </div>
    )

    /* -------------------------------- dock render -------------------------------- */

    const dockShadow =
        '0 30px 80px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(0,0,0,0.18)'

    const desktopDock = (
        <div ref={dockRef} className='absolute inset-x-0 bottom-5 z-30 flex justify-center opacity-0'>
            <div
                onPointerMove={dockPointerMove}
                onPointerLeave={dockPointerLeave}
                className='flex items-end gap-1.5 rounded-[26px] border border-white/25 bg-white/[0.14] px-3 pb-3 pt-3 backdrop-blur-2xl backdrop-saturate-150'
                style={{ height: DOCK_ICON_BASE + 24, boxShadow: dockShadow }}
            >
                {DOCK_APPS.map((app, i) => {
                    const icon = (
                        <div
                            ref={(el) => {
                                dockIconEls.current[i] = el
                            }}
                            className='drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]'
                            style={{ width: DOCK_ICON_BASE, height: DOCK_ICON_BASE }}
                        >
                            <AppGlyph id={app.id} />
                        </div>
                    )

                    return (
                        <Fragment key={app.id}>
                            {(app.id === 'x' || app.id === 'trash') && (
                                <span className='mx-0.5 my-2 w-px self-stretch bg-white/[0.22]' aria-hidden='true' />
                            )}
                            <div className='group relative flex items-end'>
                                <span
                                    className='pointer-events-none absolute -top-[44px] left-1/2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-[6px] border border-white/20 bg-black/75 px-2.5 py-[5px] text-[12px] font-medium text-white opacity-0 backdrop-blur-md transition-all duration-150 group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100'
                                    style={{ boxShadow: '0 8px 18px -6px rgba(0,0,0,0.6)' }}
                                >
                                    {app.name}
                                    <span
                                        className='absolute left-1/2 top-full size-0 -translate-x-1/2'
                                        style={{
                                            borderLeft: '5px solid transparent',
                                            borderRight: '5px solid transparent',
                                            borderTop: '5px solid rgba(0,0,0,0.75)'
                                        }}
                                    />
                                </span>
                                {app.href ? (
                                    <a
                                        href={app.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={`${app.name} (opens in a new tab)`}
                                        className='block'
                                    >
                                        {icon}
                                    </a>
                                ) : (
                                    <button type='button' aria-label={app.name} onClick={() => handleDockApp(app.id)} className='block'>
                                        {icon}
                                    </button>
                                )}
                            </div>
                        </Fragment>
                    )
                })}
            </div>
        </div>
    )

    const mobileDock = (
        <div ref={dockRef} className='absolute inset-x-0 bottom-4 z-30 px-4 opacity-0'>
            <div
                className='flex w-full items-end justify-evenly rounded-[26px] border border-white/25 bg-white/[0.14] px-3 pb-3 pt-3 backdrop-blur-2xl backdrop-saturate-150'
                style={{ boxShadow: dockShadow }}
            >
                {MOBILE_PRIMARY.map((id) => {
                    const app = DOCK_APPS.find((a) => a.id === id)
                    const label = app?.name ?? id

                    if (app?.href) {
                        return (
                            <a
                                key={id}
                                href={app.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={`${label} (opens in a new tab)`}
                                className='block drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]'
                                style={{ width: MOBILE_ICON_BASE, height: MOBILE_ICON_BASE }}
                            >
                                <AppGlyph id={id} />
                            </a>
                        )
                    }

                    return (
                        <button
                            key={id}
                            type='button'
                            aria-label={label}
                            onClick={() => handleDockApp(id)}
                            className='drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]'
                            style={{ width: MOBILE_ICON_BASE, height: MOBILE_ICON_BASE }}
                        >
                            <AppGlyph id={id} />
                        </button>
                    )
                })}
                <button
                    type='button'
                    aria-label='More apps'
                    onClick={() => setGroupPanelOpen(true)}
                    className={cn(
                        'rounded-[14px] border border-white/30 bg-white/15 p-[6px] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_4px_10px_rgba(0,0,0,0.35)] backdrop-blur-md',
                        groupPanelOpen && 'ring-2 ring-white/40 ring-offset-2 ring-offset-transparent'
                    )}
                    style={{ width: MOBILE_ICON_BASE, height: MOBILE_ICON_BASE }}
                >
                    <div className='grid h-full grid-cols-3 gap-[3px]'>
                        {GROUP_APPS.map((id) => (
                            <span key={id} className='size-full overflow-hidden rounded-[3px]'>
                                <AppGlyph id={id} />
                            </span>
                        ))}
                        {Array.from({ length: 5 }).map((_, i) => (
                            <span key={`empty-${i}`} />
                        ))}
                    </div>
                </button>
            </div>
        </div>
    )

    return (
        <main
            ref={sectionRef}
            aria-label='Alex Morgan — interactive desktop portfolio'
            className='relative isolate h-dvh w-full overflow-hidden bg-[#1a1612]'
            style={{ fontFamily: 'var(--font-sans, ui-sans-serif, system-ui)' }}
        >
            <h1 className='sr-only'>Alex Morgan — Independent Product and Brand Designer</h1>

            <div ref={wallpaperWrapRef} className='absolute inset-0 z-0'>
                <div
                    className='absolute inset-0 bg-cover'
                    style={{
                        backgroundImage: `url(${WALLPAPER_LIGHT})`,
                        backgroundPosition: 'center 38%',
                        opacity: isDark ? 0 : 1,
                        transition: 'opacity 500ms ease-out'
                    }}
                />
                <div
                    className='absolute inset-0 bg-cover'
                    style={{
                        backgroundImage: `url(${WALLPAPER_DARK})`,
                        backgroundPosition: 'center 38%',
                        opacity: isDark ? 1 : 0,
                        transition: 'opacity 500ms ease-out'
                    }}
                />
            </div>

            <div className='pointer-events-none absolute inset-0 z-[1]' style={{ background: VEIL_GRADIENT }} />

            <div
                className='absolute inset-0 z-[3]'
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) setSelectedId(null)
                }}
            />

            {mounted && (
                <>
                    {isMobile ? (
                        <div className='absolute inset-x-0 bottom-[100px] top-[64px] z-[4] grid grid-cols-3 content-start gap-x-2 gap-y-4 overflow-y-auto px-4 pb-4 pt-2'>
                            {TILES.map((tile) => renderMobileTile(tile))}
                        </div>
                    ) : (
                        TILES.map((tile, i) => renderDesktopTile(tile, i))
                    )}

                    <div ref={menuBarRef} className='absolute inset-x-0 top-0 z-[5] opacity-0'>
                        <div className='hidden h-7 items-center justify-between bg-black/15 px-4 backdrop-blur-md sm:flex'>
                            <div className='flex items-center gap-4 text-[11px] text-white/80'>
                                <span className='size-3 rounded-[2px] bg-white' aria-hidden='true' />
                                <span className='font-bold'>Pro Mac</span>
                                <span className='text-white/70'>File</span>
                                <span className='text-white/70'>Edit</span>
                                <span className='text-white/70'>View</span>
                                <span className='text-white/70'>Window</span>
                                <span className='text-white/70'>Help</span>
                            </div>
                            <div className='flex items-center gap-3 text-[11px] text-white/80'>
                                <span className='text-white'>
                                    <MacBatteryGlyph />
                                </span>
                                <span>100%</span>
                                <span>Wed 14:08</span>
                            </div>
                        </div>

                        <div className='flex h-12 items-center justify-between px-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] sm:hidden'>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-[14px] font-semibold tracking-tight'>14:08</span>
                                <BellSlashGlyph />
                            </div>
                            <div className='flex items-center gap-1'>
                                <SignalBarsGlyph />
                                <WifiGlyph />
                                <IOSBatteryGlyph />
                            </div>
                        </div>
                    </div>

                    {!isMobile && !entranceDone && (
                        <div
                            ref={featuredThumbRef}
                            className='absolute left-1/2 top-1/2 z-40 size-[180px] overflow-hidden rounded-[28px] border border-white/15 bg-white opacity-0'
                            style={{ boxShadow: '0 40px 90px -12px rgba(0,0,0,0.7), 0 0 0 4px rgba(255,255,255,0.06)' }}
                        >
                            <img
                                ref={featuredImgRef}
                                src={PROJECTS[0].thumbnailSrc}
                                alt=''
                                draggable={false}
                                className={cn('size-full', PROJECTS[0].thumbnailIsIcon ? 'object-contain p-[16%]' : 'object-cover')}
                            />
                        </div>
                    )}

                    {isMobile ? mobileDock : desktopDock}

                    {groupPanelOpen && (
                        <div
                            ref={groupBackdropRef}
                            onMouseDown={(e) => {
                                if (e.target === e.currentTarget) setGroupPanelOpen(false)
                            }}
                            className='fixed inset-0 z-[45] flex items-center justify-center bg-black/35 p-6 backdrop-blur-md'
                        >
                            <div
                                ref={groupPanelRef}
                                className='w-full max-w-[360px] rounded-[28px] border border-white/[0.22] bg-white/[0.13] p-6 backdrop-blur-2xl backdrop-saturate-150'
                                style={{ boxShadow: dockShadow }}
                            >
                                <p className='text-[12px] font-semibold uppercase tracking-[0.18em] text-white/75'>More</p>
                                <div className='mt-4 grid grid-cols-3 gap-x-2 gap-y-5'>
                                    {GROUP_APPS.map((id) => {
                                        const app = DOCK_APPS.find((a) => a.id === id)
                                        return (
                                            <button
                                                key={id}
                                                type='button'
                                                onClick={() => {
                                                    handleDockApp(id)
                                                    setGroupPanelOpen(false)
                                                }}
                                                className='flex flex-col items-center gap-2'
                                            >
                                                <span
                                                    className='drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]'
                                                    style={{ width: 58, height: 58 }}
                                                >
                                                    <AppGlyph id={id} />
                                                </span>
                                                <span className='text-[11.5px] font-medium text-white/85'>{app?.name ?? id}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='pointer-events-none absolute inset-0 z-[50]'>
                        {windows.map((win) => (
                            <WindowView
                                key={win.uid}
                                win={win}
                                ctx={ctx}
                                sectionRef={sectionRef}
                                onClose={closeWindow}
                                onFocus={focusWindow}
                                onMove={moveWindow}
                            />
                        ))}
                    </div>

                    {lightbox && <PhotoLightbox src={lightbox} onClose={() => setLightbox(null)} />}
                </>
            )}
        </main>
    )
}
